// lib imports
import * as React from "react";
import {
  Link,
  IconButton,
  Stack,
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  Paper,
  InputBase,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

// helpers and setting imports
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import LoadingPendingInterventions from "./LoadingPendingInterventions";
import EmptyPendingInterventions from "./EmptyPendingInterventions";
import BadgeList from "../common/BadgeList";
import PendingInterventionWorkflowAdvancedFilters from "./PendingInterventionWorkflowAdvancedFilters";
import { formatToUsDateFromUTCNew } from "../utils/DateHelper";
import InterventionHistoryModal from "../patient_reports/health_overview/panels/InterventionHistoryModal";

interface InterventionBadgeChip {
  key: number;
  label: string;
  type: string;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#EFE9E8",
    color: "black",
    fontSize: 14,
    borderRadius: "25px",
    padding: 0,
  },
  [`& .MuiTooltip-arrow`]: {
    color: "#EFE9E8",
    "&::before": {
      content: '""',
      display: "block",
      width: 24,
      height: 24,
      backgroundColor: "#EFE9E8",
      transform: "rotate(45deg)",
      position: "absolute",
      top: "-10px",
      left: "50%",
      marginLeft: "-8px",
    },
  },
}));

function interventionTypeBadgeKey(label: string): number {
  let h = 0;
  for (let i = 0; i < label.length; i += 1) {
    h = (Math.imul(31, h) + label.charCodeAt(i)) | 0;
  }
  return h === 0 ? 1 : h;
}

interface PendingIntervention {
  id: string;
  date: string;
  patient_id: string;
  patient_name: string;
  intervention_type: string;
  activity: string;
  related_condition: string;
  status: string;
  needs_supervisor_review: boolean;
}

const PendingInterventionWorkflow: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  // Data state
  const [interventions, setInterventions] = React.useState<PendingIntervention[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [lastNameSearchInput, setLastNameSearchInput] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const advancedFilterTriggerRef = React.useRef<HTMLDivElement | null>(null);
  const [checkedInterventionTypes, setCheckedInterventionTypes] = React.useState<string[]>([]);
  const [interventionTypeOptions, setInterventionTypeOptions] = React.useState<string[]>([]);
  const [badgeData, setBadgeData] = React.useState<InterventionBadgeChip[]>([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = React.useState<boolean>(false);
  const [selectedHistoryId, setSelectedHistoryId] = React.useState<string | null>(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);

  const tooltipText =
    "These are interventions that are still in progress. They require review or additional action before they can be signed and finalized.";

  const toggleAdvancedFilter = () => {
    if (loading) return;
    setAnchorEl((prev) =>
      prev === null ? advancedFilterTriggerRef.current : null,
    );
  };

  React.useEffect(() => {
    getPendingInterventions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, lastName, checkedInterventionTypes]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setLastName(lastNameSearchInput);
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [lastNameSearchInput]);

  const getPendingInterventions = async () => {
    setLoading(true);

    try {
      const query = new URLSearchParams();
      query.set("status", "pended");
      query.set("page", String(currentPage));
      query.set("per_page", String(perPage));
      
      if (lastName.trim().length >= 2) {
        query.set("last_name", lastName.trim());
      }
      
      checkedInterventionTypes.forEach((type) => {
        query.append("intervention_types[]", type);
      });

      const response = await fetch(`/interventions?${query.toString()}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.token),
      });

      const result = await response.json();

      if (result?.success && result?.resource) {
        const rawInterventions = result.resource.interventions || result.resource;

        if (Array.isArray(result.resource.intervention_types)) {
          const incoming = result.resource.intervention_types as string[];
          setInterventionTypeOptions((prev) =>
            [...new Set([...prev, ...incoming])].sort((a, b) => a.localeCompare(b))
          );
        }

        const mappedData = rawInterventions
          .filter((item: any) => item != null)
          .map((item: any) => {
            const formatName = (fullName: string) => {
              if (!fullName) return "";
              const nameParts = fullName.trim().split(" ");
              if (nameParts.length <= 1) return fullName;
              const firstName = nameParts[0];
              const lastNamePart = nameParts[nameParts.length - 1];
              return `${lastNamePart}, ${firstName}`;
            };

            return {
              id: String(item.id),
              date: item.occurred_on_date,
              patient_id: item.patient_id === null || item.patient_id === undefined ? "" : String(item.patient_id),
              patient_name: formatName(item.patient_name),
              intervention_type: item.intervention_type,
              activity: item.activity,
              related_condition: item.related_condition_description && item.related_condition_code
                ? `${item.related_condition_description} (${item.related_condition_code})`
                : item.related_condition_description || item.related_condition_code || "",
              status: item.status,
              needs_supervisor_review: item.is_ready_for_review,
            };
          });

        setInterventions(mappedData);
        
        if (result.resource.pagination) {
          setTotalCount(result.resource.pagination.total_count);
          setTotalPages(result.resource.pagination.total_pages);
        } else {
          setTotalCount(mappedData.length);
          setTotalPages(1);
        }
      } else {
        console.error("Failed to fetch pending interventions");
        setInterventions([]);
      }
    } catch (error) {
      console.error("Error connecting to intervention API:", error);
      setInterventions([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side resetting of page when filters change
  React.useLayoutEffect(() => {
    setCurrentPage(1);
  }, [lastName, checkedInterventionTypes]);

  React.useEffect(() => {
    setBadgeData(
      checkedInterventionTypes.map((type) => ({
        key: interventionTypeBadgeKey(type),
        label: type,
        type: "interventionType",
      })),
    );
  }, [checkedInterventionTypes]);

  const handleDeleteBadge = (badgeToDelete: InterventionBadgeChip) => {
    setCheckedInterventionTypes((prev) =>
      prev.filter((t) => t !== badgeToDelete.label),
    );
  };

  const selectPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <Box
      className="report-main-content"
      sx={{ paddingTop: "10px", paddingLeft: "120px !important" }}
    >
      <Box
        className="main-header"
        sx={{ paddingY: "24px !important", marginBottom: "26px !important" }}
      >
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px" alignItems="center">
            <Typography
              sx={{
                fontSize: "24px",
                color: "black",
                fontWeight: 700,
                lineHeight: "normal",
              }}
            >
              Pending Intervention Workflow
            </Typography>
            <HtmlTooltip
              PopperProps={{
                disablePortal: true,
              }}
              placement="top"
              arrow
              title={
                <Paper
                  elevation={3}
                  sx={{
                    py: 2,
                    px: 2,
                    background: "#EFE9E8",
                  }}
                >
                  <p style={{ margin: 0 }}>{tooltipText}</p>
                </Paper>
              }
              sx={{
                tooltip: {
                  backgroundColor: "white",
                  color: "black",
                },
                arrow: {
                  color: "white",
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  color: "#868382",
                  cursor: "pointer",
                  width: 20,
                  height: 20,
                }}
              />
            </HtmlTooltip>
          </Stack>
        </Box>
      </Box>
      <Box className="report-details">
        <Box
          className="sub-header"
          sx={{
            paddingX: "24px !important",
            paddingY: "17.25px !important",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.6)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">
              Intervention Results
            </Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastNameSearchInput}
                onChange={(e) => setLastNameSearchInput(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>
            <div
              ref={advancedFilterTriggerRef}
              role="button"
              tabIndex={loading ? -1 : 0}
              className="advanced-search-container"
              onClick={loading ? undefined : toggleAdvancedFilter}
              onKeyDown={
                loading
                  ? undefined
                  : (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleAdvancedFilter();
                      }
                    }
              }
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.8 : 1,
              }}
            >
              <FilterListIcon sx={{ width: 20, height: 20 }} />
              <p className="advanced-search-text">Advanced Search</p>
              {anchorEl ? (
                <ArrowDropUp sx={{ width: 24, height: 24 }} />
              ) : (
                <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
              )}
            </div>
            <PendingInterventionWorkflowAdvancedFilters
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              interventionTypeOptions={interventionTypeOptions}
              selectedInterventionTypes={checkedInterventionTypes}
              setSelectedInterventionTypes={setCheckedInterventionTypes}
              disabled={loading}
            />
          </Box>
        </Box>

        <Box className="table-section">
          {badgeData?.length > 0 && (
            <div
              className="badge-container"
              style={{ width: "auto", marginBottom: "20px" }}
            >
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}
          {loading ? (
            <LoadingPendingInterventions
              isBadgeShowing={badgeData.length > 0}
            />
          ) : (
            <>
              {interventions?.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{
                    height: badgeData.length > 0 ? "39.6vh" : "48.2vh",
                  }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow
                        sx={{
                          "& td, & th": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell align="left" sx={{ minWidth: "77px" }}>
                          Date
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "165px" }}>
                          Patient Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Intervention Type
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Activity
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Related Condition/Dx
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: "146px" }}>
                          Review Requested
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ minWidth: "138px" }}
                        ></TableCell>
                        <TableCell align="center" sx={{ minWidth: "40px" }}>
                          History
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interventions.map((intervention) => (
                        <TableRow
                          sx={{ "& td, & th": { borderBottom: "none" } }}
                          key={intervention.id}
                        >
                          <TableCell align="left">
                            <span
                              style={{ color: "#1E1E1E", fontSize: "14px" }}
                            >
                              {intervention.date
                                ? formatToUsDateFromUTCNew(intervention.date)
                                : "--"}
                            </span>
                          </TableCell>
                          <TableCell align="left">
                            <Link
                              href={`/patient_reports/${intervention.patient_id}/interventions`}
                              className="table-body-link"
                              sx={{
                                color: "#1976D2",
                                fontWeight: 500,
                                textDecoration: "none",
                                cursor: "pointer",
                              }}
                            >
                              {intervention.patient_name}
                            </Link>
                          </TableCell>
                          <TableCell align="left">
                            {intervention.intervention_type}
                          </TableCell>
                          <TableCell align="left">
                            {intervention.activity}
                          </TableCell>
                          <TableCell align="left">
                            {intervention.related_condition}
                          </TableCell>
                          <TableCell align="center">
                            {intervention.needs_supervisor_review && (
                              <span
                                style={{
                                  backgroundColor: "#FFECCB",
                                  color: "#E59E3F",
                                  padding: "4px 12px",
                                  borderRadius: "16px",
                                  fontSize: "12px",
                                  fontWeight: 400, 
                                }}
                              >
                                Review Requested
                              </span>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Link
                              underline="hover"
                              href={`/patient_reports/${intervention.patient_id}/interventions/new?intervention_id=${intervention.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                              sx={{
                                "&, &:visited, &:hover": {
                                  color: "#1976D2",
                                  textDecorationColor: "#1976D2",
                                },
                              }}
                            >
                              <span
                                style={{
                                  color: "inherit",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  marginRight: "4px",
                                }}
                              >
                                View Intervention
                              </span>
                              <OpenInNewIcon
                                sx={{ width: 16, height: 16, color: "inherit" }}
                              />
                            </Link>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View History">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedHistoryId(intervention.id);
                                  setIsHistoryModalOpen(true);
                                }}
                              >
                                <HistoryIcon
                                  sx={{
                                    color: "#4A4A4A",
                                    width: 22,
                                    height: 22,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyPendingInterventions
                  isBadgeShowing={badgeData.length > 0}
                />
              )}
            </>
          )}
          {interventions.length > 0 && !loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "white",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                id="page-size-select"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
              {totalPages > 0 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(
                    _event: React.ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    selectPage(page);
                  }}
                  boundaryCount={1}
                  siblingCount={1}
                  showFirstButton
                  showLastButton
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {isHistoryModalOpen && (
        <InterventionHistoryModal
          open={isHistoryModalOpen}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setSelectedHistoryId(null); 
          }}
          interventionId={selectedHistoryId ? parseInt(selectedHistoryId, 10) : null}
          csrfToken={authenticationSetting.csrfToken}
        />
      )}
    </Box>
  );
};

export default PendingInterventionWorkflow;